import { useEffect, useRef } from "react";

export default function NebulaIntro({ onComplete }) {
  const canvasRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;

    const ctx = canvas.getContext("2d");
    let W, H, animId;

    function resize() {
      W = canvas.width = stage.offsetWidth;
      H = canvas.height = stage.offsetHeight;
    }
    resize();
    window.addEventListener("resize", resize);

    function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
    function lerp(a, b, t) { return a + (b - a) * t; }
    function easeOut3(t) { return 1 - Math.pow(1 - t, 3); }
    function easeIn2(t) { return t * t; }
    function easeInOut(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }

    const bands = [
      { r: 160, g: 0,   b: 0   },
      { r: 210, g: 30,  b: 0   },
      { r: 255, g: 70,  b: 0   },
      { r: 255, g: 120, b: 0   },
      { r: 255, g: 175, b: 20  },
      { r: 255, g: 230, b: 80  },
      { r: 255, g: 255, b: 200 },
      { r: 255, g: 180, b: 230 },
      { r: 210, g: 100, b: 255 },
      { r: 80,  g: 140, b: 255 },
      { r: 30,  g: 200, b: 240 },
      { r: 0,   g: 220, b: 180 },
    ];

    function drawStreaks(p) {
      ctx.fillStyle = "rgb(4,2,14)";
      ctx.fillRect(0, 0, W, H);
      const ep = easeInOut(p);
      bands.forEach((col, i) => {
        const t = i / (bands.length - 1);
        const sx = -20, sy = H * 0.48 + (t - 0.5) * 20;
        const cp1x = W * lerp(0.05, 0.2,  ep);
        const cp1y = H * lerp(0.48, 0.04 + t * 0.12, ep);
        const cp2x = W * lerp(0.5,  0.68, ep);
        const cp2y = H * lerp(0.48, 0.40, ep);
        const ex = W * 1.08, ey = H * (0.43 + t * 0.05);
        const lw = (i >= 4 && i <= 7) ? 3 : (i >= 3 && i <= 8) ? 2 : 1.2;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ex, ey);
        ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},0.85)`;
        ctx.lineWidth = lw; ctx.lineCap = "round"; ctx.stroke();
        ctx.lineWidth = lw * 6;
        ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},0.15)`;
        ctx.stroke();
      });
    }

    function drawBeam(p) {
      const bgA = clamp(p * 2.5, 0, 1);
      ctx.fillStyle = `rgb(${Math.round(lerp(4,0,bgA))},${Math.round(lerp(2,0,bgA))},${Math.round(lerp(14,0,bgA))})`;
      ctx.fillRect(0, 0, W, H);
      const alpha = 1 - easeIn2(p);
      const cy = H * 0.47;
      bands.forEach((col, i) => {
        const yOff = (i - bands.length / 2) * 2.2;
        const grd = ctx.createLinearGradient(0, cy, W, cy);
        grd.addColorStop(0,    `rgba(${col.r},${col.g},${col.b},0)`);
        grd.addColorStop(0.08, `rgba(${col.r},${col.g},${col.b},${alpha})`);
        grd.addColorStop(0.5,  `rgba(255,255,255,${alpha})`);
        grd.addColorStop(0.92, `rgba(${col.r},${col.g},${col.b},${alpha})`);
        grd.addColorStop(1,    `rgba(${col.r},${col.g},${col.b},0)`);
        ctx.beginPath();
        ctx.moveTo(0, cy + yOff); ctx.lineTo(W, cy + yOff);
        ctx.strokeStyle = grd;
        ctx.lineWidth = (i >= 4 && i <= 7) ? 4 : 1.8; ctx.stroke();
      });
      const loopA = alpha * clamp(1 - p * 2.5, 0, 1);
      if (loopA > 0.01) {
        ctx.beginPath();
        ctx.ellipse(W * 0.28, cy - 42, 58, 44, -0.28, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,165,45,${loopA * 0.9})`; ctx.lineWidth = 1.8; ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(W * 0.31, cy - 18, 34, 26, 0.18, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,210,70,${loopA * 0.55})`; ctx.lineWidth = 1.1; ctx.stroke();
      }
    }

    // Timeline (frames at 60fps):
    // 0–20   : black lead-in
    // 20–90  : streaks sweep across
    // 90–150 : beam fades out
    // 150–200: black
    // 200–260: brand fade in
    // 260+   : done → onComplete

    let f = 0;
    const brandEl = document.getElementById("nebula-brand");

    function tick() {
      if (f < 20) {
        ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
      } else if (f < 90) {
        drawStreaks((f - 20) / 70);
      } else if (f < 150) {
        drawBeam((f - 90) / 60);
      } else if (f < 200) {
        ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
      } else if (f < 260) {
        ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
        const tp = easeOut3(clamp((f - 200) / 50, 0, 1));
        if (brandEl) {
          brandEl.style.opacity = tp;
          brandEl.style.transform = `translate(-50%, calc(-50% + ${lerp(18, 0, tp)}px))`;
        }
      } else {
        window.removeEventListener("resize", resize);
        setTimeout(() => { if (onComplete) onComplete(); }, 1000);
        return;
      }
      f++;
      animId = requestAnimationFrame(tick);
    }

    animId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, [onComplete]);

  return (
    <div
      ref={stageRef}
      style={{
        position: "fixed", inset: 0, background: "#000", zIndex: 9999,
        overflow: "hidden",
      }}
    >
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />

      {/* Brand logo — shown at end of animation */}
      <div
        id="nebula-brand"
        style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, calc(-50% + 18px))",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
          opacity: 0, textAlign: "center",
        }}
      >
        <div style={{
          width: 88, height: 88, borderRadius: 22,
          background: "linear-gradient(145deg,#5b8ef0 0%,#38c6c0 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 8px 40px rgba(80,160,240,0.5)",
          fontSize: 42, fontWeight: 700, color: "#fff",
          fontFamily: "Inter, sans-serif",
        }}>N</div>

        <div style={{
          fontFamily: "Inter, sans-serif", fontSize: 26, fontWeight: 700,
          color: "#fff", letterSpacing: "0.3px", whiteSpace: "nowrap",
        }}>Nebula Finance AI</div>

        <div style={{
          fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 400,
          color: "rgba(255,255,255,0.5)", lineHeight: 1.6, maxWidth: 280,
        }}>
          Access real-time FX rates, treasury insights,<br />
          and comprehensive financial tools.
        </div>
      </div>
    </div>
  );
}
